<script lang="ts">
  import { get } from "svelte/store";
  import { formatDate } from "../../../helpers/helpers.js";
  import { getCustomers } from "../../../services/api/data-handler.js";
  import type { Customer, CustomersData } from "../../../services/api/types.js";
  import { onMount } from "svelte";
  import { createGrid } from "ag-grid-community";
  import type { GridOptions, GridApi } from "ag-grid-community";
  import { navigateTo } from "../../../services/routing/route-action.js";
  import { theme } from "../../../services/stores/theme-store.js";
  import GridHeader from "../../common/GridHeader.svelte";
  import { getSVGIcon } from "../../../services/utils.js";
  import DeleteObjectDialog from "../../dialogs/DeleteObjectDialog.svelte";
  import { userStore } from "../../../services/stores/user-store.js";
  import { drawerStore, editObject } from "../../../services/stores/object-drawer-store.js";
  import { formatToProperCase } from "../../../helpers/helpers.js";
  import { themeQuartz } from 'ag-grid-community';

  let deleteDialogOpen = false;
  let objectToDelete: any = null;

  let gridOptions: GridOptions;
  let gridApi: GridApi;

  let customersData: CustomersData;
  let customers: Customer[] = [];
  let canManageCustomers = false;
  let canViewResults = false;
  let canViewCodes = false;

  onMount(async () => {
    const store = get(userStore);
    canManageCustomers = store.claims?.canManageCustomers ?? false;
    canViewResults = store.claims?.canViewResults ?? false;
    canViewCodes = store.claims?.canViewCodes ?? false;

    await loadCustomers();

    gridOptions = {
      theme: themeQuartz,
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
      },
      autoSizeStrategy: {
        type: "fitCellContents",
      },
      columnDefs: [
        {
          field: "name",
          headerName: "Name",
          cellRenderer: (params: any) => {
            return createNameCell(params);
          },
        },
        ...(canViewCodes
          ? [
              {
                field: "code",
                headerName: "Code",
                hide: true,
                cellRenderer: (params: any) => {
                  const span = document.createElement("span");
                  span.setAttribute("data-testid", `customer-code-${params.value}`);
                  span.textContent = params.value;
                  return span;
                },
              },
            ]
          : []),
        {
          field: "projects",
          headerName: "Projects",
          sortable: true,
          filter: false,
          cellRenderer: (params: any) => {
            return createResultsCell(params);
          },
        },
        {
          field: "metadata.customer_status.name",
          headerName: "Status",
          enableRowGroup: true,
          cellRenderer: (params: any) => {
            return createStatusCell(params);
          },
          filter: "agSetColumnFilter",
          filterParams: {
            excelMode: "mac",
          },
        },
        {
          field: "last_modified",
          headerName: "Last Modified",
          sortable: false,
          filter: false,
          cellRenderer: (params: any) => {
            return createModifiedCell(params, []);
          },
        }
      ],
      groupDisplayType: "groupRows",
      rowGroupPanelShow: "always",
      onGridReady: (params) => {
        if (customers.length > 0) {
          updateGridData();
        }
      },
    };

    const gridElement = document.querySelector("#customerGrid") as HTMLElement;
    gridApi = createGrid(gridElement, gridOptions);

    // Add click handler for customer links
    gridElement.addEventListener("click", (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName === "A") {
        event.preventDefault();
        const customer_id = target.getAttribute("data-customer-id");
        if (customer_id) {
          onOpenClick(customer_id);
        }
      }
    });
  });

  function onOpenClick(customer_id: string) {
    navigateTo("customer", customer_id);
  }

  async function onDeleteClick(customer_id: string) {
    objectToDelete = customers.find((c) => c.customer_id === customer_id);
    if (objectToDelete) {
      deleteDialogOpen = true;
    }
  }

  function onViewResultsClick(customer_id: string) {
    navigateTo("view-results", customer_id);
  }

  function onEditClick(customer_id: string) {
    editObject("customer", customer_id);
  }

  function createNameCell(params: any) {
    const name = `<a href="#" data-customer-id="${params.data.customer_id}">${params.data.name}</a>`;
    const projects = params.data.projects;
    const span = document.createElement("span");
    span.classList.add("grid-cell");
    span.innerHTML = name;

    const buttonConfigs = [
      {
        type: "edit",
        icon: "edit",
        onClick: onEditClick,
        isVisible: () => {
          return canManageCustomers;
        },
      },
      {
        type: "delete",
        icon: "delete",
        onClick: onDeleteClick,
        isVisible: () => {
          return canManageCustomers && projects === 0;
        },
      },
    ];

    // Filter buttons based on visibility rules
    const visibleButtons = buttonConfigs.filter((btn) => btn.isVisible());
    // Only create button group if there are visible buttons
    if (visibleButtons.length > 0) {
      const buttonGroup = document.createElement("span");
      buttonGroup.classList.add("grid-button-group");
      addActionButtons(buttonGroup, params, visibleButtons);
      span.appendChild(buttonGroup);
    }
    return span;
  }

  function createResultsCell(params: any) {
    const span = document.createElement("span");
    span.classList.add("grid-cell");

    const projects = params.data.projects;
    if (projects === 0) {
      span.innerHTML = "None";
    } else {
      const span1 = document.createElement("span");
      span1.classList.add("grid-cell", "main");
      span1.innerHTML = `${projects}`;
      span.appendChild(span1);

      const projects_with_results = params.data.projects_with_results;
      if (projects_with_results > 0) {
        const span2 = document.createElement("span");
        span2.classList.add("grid-cell");
        const label2 = document.createElement("span");
        label2.classList.add("label");
        const content2 = document.createElement("span");
        label2.textContent = "With Results:";
        content2.innerHTML = `${params.data.projects_with_results}`;
        span2.appendChild(label2);
        span2.appendChild(content2);
        span.appendChild(span2);

        const buttonConfigs = [
          {
            type: "view-results",
            text: "View",
            icon: "view",
            onClick: onViewResultsClick,
            isVisible: () => {
              return canViewResults;
            },
          },
        ];

        // Filter buttons based on visibility rules
        const visibleButtons = buttonConfigs.filter((btn) => btn.isVisible());

        // Only create button group if there are visible buttons
        if (visibleButtons.length > 0) {
          const buttonGroup = document.createElement("span");
          buttonGroup.classList.add("grid-button-group");
          addActionButtons(buttonGroup, params, visibleButtons);
          span.appendChild(buttonGroup);
        }
      }
    }
    return span;
  }

  function createModifiedCell(params: any, buttonConfigs: any) {
    const span = document.createElement("span");
    span.classList.add("grid-cell", "modified-cell");
    const content = document.createElement("div");

    if (!params.data.updated_at) {
      span.classList.add("created");
      const date = formatDate(params.data.created_at);
      content.textContent = `${params.data.created_by_name} (${date})`;
    } else {
      span.classList.add("updated");
      const date = formatDate(params.data.updated_at);
      content.textContent = `${params.data.updated_by_name} (${date})`;
    }
    span.appendChild(content);
    return span;
  }

  function createStatusCell(params: any) {
    const status = formatToProperCase(
      params.data.metadata.customer_status.name
    );
    const span = document.createElement("span");
    const span1 = document.createElement("span");
    span.classList.add("grid-cell", "status-container");
    span1.classList.add("status", status);
    span1.innerHTML = status;
    span.appendChild(span1);
    return span;
  }

  function addActionButtons(span: any, params: any, buttonConfigs: any) {
    buttonConfigs.forEach((config: any) => {
      const button = document.createElement("button");
      button.classList.add("grid-button", `${config.type}-button`);
      button.setAttribute("data-testid", `customer-name-${params.data.code}`);
      button.innerHTML = getSVGIcon(config.icon);
      if (config.text) {
        button.innerHTML += config.text;
        button.classList.add("text");
      }
      button.addEventListener("click", () =>
        config.onClick(params.data.customer_id)
      );
      span.appendChild(button);
    });
    return span;
  }

  async function loadCustomers() {
    customersData = await getCustomers();
    customers = customersData.customers;
    updateGridData();
  }

  function updateGridData() {
    if (gridApi && customers) {
      gridApi.setGridOption("rowData", customers);
      setTimeout(() => {
        gridApi.sizeColumnsToFit();
        gridApi.autoSizeAllColumns();
      }, 100);
    }
  }

  $: if ($drawerStore.object && $drawerStore.objectType === "customer") {
    loadCustomers();
  }

  // Update theme mode on the grid container when theme changes
  $: {
    const gridElement = document.querySelector("#customerGrid");
    if (gridElement) {
      gridElement.setAttribute("data-ag-theme-mode", $theme);
    }
  }
</script>


<GridHeader
  objectType="customer"
  title="Customers"
  on:refresh={() => loadCustomers()}
  canAdd={canManageCustomers}
/>
<div id="customerGrid" class="ag-theme-quartz ag-grid" data-ag-theme-mode="{$theme === 'dark' ? 'dark' : 'light'}"></div>
<DeleteObjectDialog
  bind:open={deleteDialogOpen}
  objectType="customer"
  object={objectToDelete}
  on:delete={async (event) => {
    if (event.detail?.result) {
      customers = customers.filter(
        (c) =>
          c.customer_id !== objectToDelete.customer_id && c !== objectToDelete
      );
      updateGridData();

      // Clear the drawer store
      $drawerStore = {
        id: "customer-grid",
        instanceId: "customer-grid",
        open: false,
        action: null,
        objectType: null,
        object: null,
      };
      deleteDialogOpen = false;
      objectToDelete = null;
    }
  }}
  on:cancel={() => {
    deleteDialogOpen = false;
    objectToDelete = null;
  }}
/>
